import { Component, Vue } from 'vue-property-decorator';
import moment from 'moment';
import { ASSESSMENT_PERIOD_STATUSES, DATE_FORMAT, DEFAULT_DISPLAY_VALUE, PAGE_NAMES } from '@/constants';
import { convertNumberToFourDigitsString, formatDateToLocalDateTime } from '@/helpers';
import { AssessmentPeriodDTO, DisplayStatus, ProjectDTO, TableHeaderColumns, UserDTO } from '@/models';
import { createNewAssessmentPeriod, fetchAssessmentPeriods, fetchProjectsOfCurrentUser } from '@/services';
import AssessmentPeriodForm from '@/components/pages/assessment-periods/assessment-periods-list/assessment-period-form/AssessmentPeriodForm.vue';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';
import SearchInput from '@/components/common/search-input/SearchInput.vue';
import Status from '@/components/common/status/Status.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';
import AssessmentPeriodExport from './assessment-period-export/AssessmentPeriodExport.vue';

@Component({
  components: {
    AssessmentPeriodForm,
    AssessmentPeriodExport,
    InlineLoader,
    SearchInput,
    Status,
    Pagination
  }
})
export default class AssessmentPeriodsList extends Vue {
  private currentUser: UserDTO = {} as UserDTO;
  private isAssessmentPeriodsFetching = true;
  private isCreateNew = false;
  private headers: TableHeaderColumns = [
    {
      text: 'ID',
      align: 'start',
      value: 'id'
    },
    { text: 'Name', value: 'name' },
    { text: 'From', value: 'startDate', filterable: false },
    { text: 'To', value: 'endDate', filterable: false },
    { text: 'Number of projects', value: 'numOfProject', align: 'end', filterable: false },
    { text: 'Status', value: 'status', filterable: false },
    { text: 'Created at', value: 'createdAt', align: 'center', filterable: false },
    { text: '', value: 'detail', align: 'end', sortable: false, filterable: false }
  ];
  private assessmentPeriods: AssessmentPeriodDTO[] = [];
  private search = '';
  private page = 1;
  private pageCount = 0;
  private itemsPerPage = 10;
  private projects: ProjectDTO[] = [];
  private modalShown = false;

  openCreateNewDialog(): void {
    this.isCreateNew = true;
  }

  closeCreateNewDialog(): void {
    this.isCreateNew = false;
  }

  displayID(id: number): string {
    return convertNumberToFourDigitsString(id);
  }

  displayDate(date: string): string {
    if (date) {
      return moment(date).format(DATE_FORMAT.DISPLAY);
    }
    return DEFAULT_DISPLAY_VALUE.DATE;
  }

  isHaveOnGoingPeriod(): boolean {
    if (this.assessmentPeriods.length !== 0) {
      const checkPeriod = this.assessmentPeriods.filter(
        item => item.status === ASSESSMENT_PERIOD_STATUSES.ON_GOING.label
      );
      if (checkPeriod.length === 1) {
        return true;
      }
    }
    return false;
  }

  filterByNameAndId(value: keyof AssessmentPeriodDTO, search: string, item: AssessmentPeriodDTO): boolean {
    return (
      value !== null &&
      search !== null &&
      (convertNumberToFourDigitsString(item.id || -1).includes(search) || item.name.includes(search))
    );
  }

  created(): void {
    this.currentUser = this.$store.getters['app/currentUser'];
  }

  mounted(): void {
    fetchAssessmentPeriods()
      .then(payload => {
        this.assessmentPeriods = payload.data || [];
      })
      .finally(() => {
        this.isAssessmentPeriodsFetching = false;
      });
    fetchProjectsOfCurrentUser().then(payload => {
      this.projects = payload.data || [];
    });
  }

  async createNewPeriod(name: string, description: string, startDate: string, endDate: string): Promise<void> {
    await createNewAssessmentPeriod({
      name: name,
      description: description,
      startDate: formatDateToLocalDateTime(startDate),
      endDate: formatDateToLocalDateTime(endDate),
      createdById: this.currentUser.id
    }).then(payload => {
      if (payload.data) {
        this.assessmentPeriods.push(payload.data);
        this.$store.commit('app/setIsAssessmentPeriodJustCreated', true);
        this.navigateToAssessmentPeriodsDetail(payload.data.id as number);
      }
      this.isCreateNew = false;
    });
  }

  async navigateToAssessmentPeriodsDetail(id: number): Promise<void> {
    await this.$router.push({
      name: PAGE_NAMES.ASSESSMENT_PERIOD_DETAIL,
      params: {
        id: '' + id
      }
    });
  }

  onSearch(searchText: string): void {
    this.search = searchText;
  }

  getDisplayStatus(status: string): DisplayStatus {
    return ASSESSMENT_PERIOD_STATUSES[status];
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }

  onExport(): void {
    if (this.assessmentPeriods?.length) {
      this.modalShown = true;
    }
  }
}
